import { useState } from "react";
import { WarningCircle, LockKey, X } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

// Password-gated delete confirmation. Calls DELETE with { password } in body.
export default function DeleteShipmentModal({ shipment, onClose, onDeleted }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!password) { setErr("Password is required"); return; }
    setBusy(true);
    try {
      await api.delete(`/shipments/${shipment.id}`, { data: { password } });
      toast.success(`Shipment ${shipment.awb_number} deleted`);
      onDeleted?.();
      onClose?.();
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose} data-testid="delete-shipment-modal">
      <div className="bg-white border border-zinc-200 shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between px-5 py-4 border-b border-zinc-200">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 bg-red-600 text-white flex items-center justify-center flex-shrink-0">
              <WarningCircle size={18} weight="fill" />
            </div>
            <div>
              <div className="font-display font-black text-lg text-zinc-950 tracking-tight leading-tight">Delete shipment?</div>
              <div className="text-xs text-zinc-500 mt-0.5">This action cannot be undone.</div>
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-900" aria-label="Close" data-testid="delete-modal-close">
            <X size={16} />
          </button>
        </div>

        <div className="px-5 py-4 border-b border-zinc-100 bg-zinc-50">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Shipment</div>
          <div className="font-mono text-lg text-zinc-900 mt-1" data-testid="delete-modal-awb">{shipment.awb_number}</div>
          {shipment.consignee && <div className="text-xs text-zinc-500 mt-0.5">{shipment.consignee}</div>}
          <div className="mt-2 text-xs text-red-700">
            All attached documents will also be removed.
          </div>
        </div>

        <form onSubmit={submit} className="px-5 py-4 space-y-3" data-testid="delete-shipment-form">
          <div>
            <label className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 block mb-1.5">
              Confirm your password
            </label>
            <div className="relative">
              <LockKey size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input
                type="password" required autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your account password"
                className="w-full pl-9 pr-3 py-2.5 text-sm border border-zinc-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                data-testid="delete-password-input"
              />
            </div>
          </div>

          {err && (
            <div data-testid="delete-error" className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2">
              {err}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-1">
            <button type="button" onClick={onClose}
              className="px-3 py-2 text-sm border border-zinc-200 bg-white hover:bg-zinc-100 transition-colors duration-150"
              data-testid="delete-cancel-btn">
              Cancel
            </button>
            <button type="submit" disabled={busy}
              className="px-4 py-2 text-sm bg-red-600 text-white hover:bg-red-700 disabled:opacity-60 transition-colors duration-150 flex items-center gap-2"
              data-testid="delete-confirm-btn">
              <WarningCircle size={14} weight="fill" /> {busy ? "Deleting…" : "Delete shipment"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
